import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * ds-header-menu-blank-panel v1 :: teardown callbacks for pending submenu height
   * re-measurements (animation frames, the observer's timer, image listeners).
   * @type {(() => void)[]}
   */
  #submenuWatchers = [];

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);

    // ds-header-menu-blank-panel v1 :: this module is loaded with fetchpriority="low", so a pointer
    // can already be resting on a trigger by the time it runs. pointerenter does not replay, which
    // left the menu dead until the pointer moved out and back in. Replay that hover once, on the
    // frame after hydration so `:hover` has settled. Fine-pointer only — touch devices get the drawer.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      requestAnimationFrame(() => {
        const hovered = this.querySelector('.menu-list__list-item:hover');
        if (hovered instanceof HTMLElement) this.activate(/** @type {any} */ ({ target: hovered }));
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupSubmenuWatchers();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup anything still pending from a previous menu activation
      this.#cleanupSubmenuWatchers();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => this.#remeasureSubmenu(item, submenu));
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      const observerTimer = setTimeout(() => this.#cleanupMutationObserver(), 500);
      this.#submenuWatchers.push(() => clearTimeout(observerTimer));

      // ds-header-menu-blank-panel v1 :: the observer above only fires when the submenu's DOM changes,
      // which never happens for server-rendered menu markup — so the height below was written once and
      // never corrected. Re-measure unconditionally, and again whenever a menu image finishes loading:
      // the featured products use aspect ratio "adapt", so they reserve no box until they load and the
      // real height moves after the first measurement.
      this.#remeasureSubmenu(item, submenu);

      for (const image of submenu.querySelectorAll('img')) {
        if (image.complete) continue;

        const onSettled = () => this.#remeasureSubmenu(item, submenu);

        image.addEventListener('load', onSettled, {once: true});
        image.addEventListener('error', onSettled, {once: true});
        this.#submenuWatchers.push(() => {
          image.removeEventListener('load', onSettled);
          image.removeEventListener('error', onSettled);
        });
      }
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    // A pending re-measurement must never re-open the panel after it has closed.
    this.#cleanupSubmenuWatchers();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  /**
   * ds-header-menu-blank-panel v1 :: re-measure a submenu two frames after it changed and correct
   * `--submenu-height` if it moved. Two frames because content-visibility and late-loading images
   * only settle at a rendering opportunity, not on the forced layout that `offsetHeight` triggers.
   * @param {HTMLElement} item - The menu item that owns the submenu.
   * @param {HTMLElement} submenu
   */
  #remeasureSubmenu(item, submenu) {
    const outerFrame = requestAnimationFrame(() => {
      const innerFrame = requestAnimationFrame(() => {
        // Never fight #deactivate() — only correct the submenu that is still open.
        if (this.#state.activeItem !== item) return;

        const height = submenu.offsetHeight;

        if (height <= 0) return;
        if (this.headerComponent?.style.getPropertyValue('--submenu-height') === `${height}px`) return;

        this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
      });

      this.#submenuWatchers.push(() => cancelAnimationFrame(innerFrame));
    });

    this.#submenuWatchers.push(() => cancelAnimationFrame(outerFrame));
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Cancel the mutation observer plus every pending re-measurement and image listener.
   */
  #cleanupSubmenuWatchers() {
    this.#cleanupMutationObserver();

    for (const teardown of this.#submenuWatchers.splice(0)) teardown();
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
